import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
} from '@wundo/ui';

export interface NewTopicSetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  saving: boolean;
  /** Shown in the dialog when the last attempt failed; empty while it has not. */
  error: string;
  onCreate: (name: string) => void;
}

/**
 * A topic set starts as a name and nothing else — the themes are written on the
 * set's own page, which is where creating one lands.
 */
export function NewTopicSetDialog({
  open,
  onOpenChange,
  saving,
  error,
  onCreate,
}: NewTopicSetDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size='md'>
        {/* Mounted with the dialog, so the field opens empty every time. */}
        {open && (
          <NewTopicSetForm
            saving={saving}
            error={error}
            onCreate={onCreate}
            onCancel={() => onOpenChange(false)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function NewTopicSetForm({
  saving,
  error,
  onCreate,
  onCancel,
}: Pick<NewTopicSetDialogProps, 'saving' | 'error' | 'onCreate'> & {
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const submittable = name.trim() !== '';

  return (
    <>
      <DialogHeader>
        <DialogTitle>New topic set</DialogTitle>
      </DialogHeader>

      <div className='flex flex-col gap-4 px-5 py-4'>
        <Field label='Name'>
          {(control) => (
            <Input
              {...control}
              autoFocus
              placeholder='e.g. Leiderschapskader 2026'
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          )}
        </Field>
        {error !== '' && (
          <p role='alert' className='text-sm text-danger'>
            {error}
          </p>
        )}
      </div>

      <DialogFooter>
        <Button size='sm' variant='quiet' disabled={saving} onClick={onCancel}>
          Cancel
        </Button>
        <Button
          size='sm'
          disabled={!submittable}
          loading={saving}
          onClick={() => submittable && onCreate(name.trim())}
        >
          Create
        </Button>
      </DialogFooter>
    </>
  );
}
